import type { BlockKind, LoadType, Modality, SetType } from './enums.js';
import type { LoggedSet, PrescribedSet } from './types.js';

/**
 * Turning a plan into today's workout.
 *
 * This is the moment a *template* becomes a *performance*. The plan says
 * "3 × 8–12"; today's workout is three editable rows that start there and end
 * up recording what actually happened.
 *
 * Two properties matter more than anything else here:
 *
 *  1. **The plan is only ever read.** Nothing in this module returns a plan
 *     row, references one by identity, or offers any way to write one back. The
 *     session that comes out is a separate structure with its own ids, and the
 *     prescription is copied in as a frozen snapshot. A workout physically
 *     cannot alter the plan it came from.
 *
 *  2. **It is pure and deterministic.** Ids and timestamps arrive as arguments,
 *     never from a generator or a clock inside this file. That is what lets the
 *     phone build a session offline and the PC rebuild an identical one later
 *     from the same inputs — the guarantee the whole offline story rests on.
 */

/** A plan exercise as the phone holds it, straight from the bundle. */
export interface PlanExerciseSource {
  planExerciseId: string;
  exerciseId: string;
  orderInBlock: number;
  modality: Modality;
  loadType: LoadType;
  restSeconds: number | null;
  /** Already normalised to per-set rows by `expandPrescription`. */
  sets: readonly PrescribedSet[];
}

export interface PlanBlockSource {
  planBlockId: string;
  orderIndex: number;
  kind: BlockKind;
  restSeconds: number | null;
  exercises: readonly PlanExerciseSource[];
}

export interface PlanDaySource {
  planId: string;
  planDayId: string;
  name: string;
  blocks: readonly PlanBlockSource[];
}

/**
 * The prescription, preserved verbatim alongside the workout.
 *
 * Stored on the session exercise so history can always answer "what was asked
 * for?" as well as "what was done?" — even if the plan is later edited or
 * deleted. It is a snapshot, never a reference.
 */
export interface PrescriptionSnapshot {
  planExerciseId: string;
  restSeconds: number | null;
  sets: PrescribedSet[];
}

export interface SessionSetDraft extends LoggedSet {
  /** Position among primary sets, for display. Not persisted. */
  displayIndex: number;
}

/**
 * One materialised cardio segment — Cardio Phase C3.
 *
 * Exactly the fields `CardioSegmentRecord` has, and no more. There are
 * deliberately no target columns here: the prescription lives once, on the
 * session exercise's `prescribed` snapshot, and is never copied into a second
 * place that could drift from it.
 */
export interface CardioSegmentDraft {
  id: string;
  sessionExerciseId: string;
  orderIndex: number;
  durationS: number | null;
  distanceM: number | null;
  avgHeartRate: number | null;
  isCompleted: boolean;
}

export interface SessionExerciseDraft {
  id: string;
  sessionId: string;
  sessionBlockId: string;
  exerciseId: string;
  orderIndex: number;
  status: 'pending' | 'active' | 'completed' | 'skipped';
  modality: Modality;
  loadType: LoadType;
  restSeconds: number | null;
  sourcePlanExerciseId: string | null;
  prescribed: PrescriptionSnapshot;
  /** Empty for a cardio exercise — cardio logs segments, never sets. */
  sets: SessionSetDraft[];
  /** Empty for strength and hold. Populated only for `modality: 'cardio'`. */
  segments: CardioSegmentDraft[];
}

export interface SessionBlockDraft {
  id: string;
  sessionId: string;
  orderIndex: number;
  kind: BlockKind;
  restSeconds: number | null;
  sourcePlanBlockId: string | null;
  exercises: SessionExerciseDraft[];
}

export interface SessionDraft {
  id: string;
  planId: string | null;
  planDayId: string | null;
  name: string;
  status: 'active';
  startedAt: number;
  endedAt: null;
  localDate: string;
  tzOffsetMin: number;
  blocks: SessionBlockDraft[];
}

export interface MaterialiseOptions {
  /**
   * Supplies every id. The caller owns identity — on the phone these are v4
   * UUIDs minted locally so a session can be created with no network, and the
   * server accepts them unchanged, which is what makes a replayed sync event a
   * no-op rather than a duplicate.
   */
  newId: () => string;
  /** Session start. Epoch milliseconds, UTC. */
  startedAt: number;
  /** 'YYYY-MM-DD' in the timezone the workout is happening in. */
  localDate: string;
  tzOffsetMin: number;
  /** Defaults to the plan day's name. */
  name?: string;
}

/**
 * Seed a set row from its prescription.
 *
 * The target weight is carried across as a starting value, but reps are
 * deliberately **not**: "8–12" is a range, and pre-filling one end of it would
 * be inventing a number the user did not perform. An empty field that must be
 * filled in is honest; a wrong number that looks filled in is not.
 */
/**
 * One prescribed row becomes one cardio segment — Cardio Phase C3.
 *
 * Duration and distance are pre-filled from the prescription, exactly as
 * `seedSet` below pre-fills `weightG` from `targetWeightG` and `durationS`
 * from `targetDurationS`. Cardio is treated as the codebase already treats a
 * timed hold rather than as a special case: the prescribed number is the
 * starting value the athlete adjusts, and it counts for nothing until the
 * segment is actually marked complete (`summariseCardio` and
 * `detectPersonalRecords` both ignore incomplete segments).
 *
 * `avgHeartRate` starts null because no prescription field feeds it.
 */
function seedSegment(
  prescribed: PrescribedSet,
  context: { id: string; sessionExerciseId: string; orderIndex: number },
): CardioSegmentDraft {
  return {
    id: context.id,
    sessionExerciseId: context.sessionExerciseId,
    orderIndex: context.orderIndex,
    durationS: prescribed.targetDurationS,
    distanceM: prescribed.targetDistanceM,
    avgHeartRate: null,
    isCompleted: false,
  };
}

function seedSet(
  prescribed: PrescribedSet,
  context: { id: string; sessionExerciseId: string; loadType: LoadType; displayIndex: number },
): SessionSetDraft {
  return {
    id: context.id,
    sessionExerciseId: context.sessionExerciseId,
    setIndex: prescribed.setIndex,
    dropIndex: prescribed.dropIndex,
    setType: prescribed.setType,
    parentSetId: null,

    loadType: context.loadType,
    weightG: prescribed.targetWeightG,
    bodyweightG: null,
    bodyweightSource: 'unknown',
    addedWeightG: null,
    assistWeightG: null,

    reps: null,
    durationS: prescribed.targetDurationS,
    rpe: null,

    isCompleted: false,
    completedAt: null,

    displayIndex: context.displayIndex,
  };
}

/**
 * Build today's workout from a plan day.
 *
 * The result is a complete, editable session: every prescribed set becomes a
 * row waiting to be filled in, and the prescription travels alongside as a
 * snapshot rather than a pointer.
 */
export function materialiseSession(
  day: PlanDaySource,
  options: MaterialiseOptions,
): SessionDraft {
  const sessionId = options.newId();

  const blocks: SessionBlockDraft[] = [];
  // Exercise order runs across the whole session, not per block: it is what
  // `uq_session_exercise_order` constrains, and what the UI walks through.
  let exerciseOrder = 0;

  for (const block of [...day.blocks].sort((a, b) => a.orderIndex - b.orderIndex)) {
    const blockId = options.newId();
    const exercises: SessionExerciseDraft[] = [];

    for (const exercise of [...block.exercises].sort((a, b) => a.orderInBlock - b.orderInBlock)) {
      const exerciseId = options.newId();
      exerciseOrder += 1;

      const ordered = [...exercise.sets].sort(
        (a, b) => a.setIndex - b.setIndex || a.dropIndex - b.dropIndex,
      );

      // ── The modality branch (Cardio Phase C3) ──────────────────────────
      //
      // Cardio logs SEGMENTS, strength and hold log SETS, and no exercise
      // ever gets both. Creating placeholder sets for cardio would double-
      // count it: `strengthVolume` and `countCompletedWorkingSets` iterate
      // sets, and a cardio row appearing there would put a 4 km run into a
      // kilogram figure — precisely what PHASE-0-ARCHITECTURE §399 forbids.
      //
      // One segment per prescribed row, not one per exercise. For the common
      // "run 5 km" that IS one segment, because the prescription is one row.
      // A per-set ladder (400/800/1600 intervals, which `expandPrescription`
      // has supported since C1) therefore materialises as three segments
      // instead of silently collapsing to one.
      const isCardio = exercise.modality === 'cardio';

      let displayIndex = 0;
      const sets = isCardio
        ? []
        : ordered.map((prescribed) => {
            if (prescribed.dropIndex === 0) displayIndex += 1;
            return seedSet(prescribed, {
              id: options.newId(),
              sessionExerciseId: exerciseId,
              loadType: exercise.loadType,
              displayIndex,
            });
          });

      // `orderIndex` is 0-based and contiguous here, matching what
      // `sessionRepository.addSegment` computes for a segment added later
      // (max over live siblings, + 1).
      const segments = isCardio
        ? ordered.map((prescribed, index) =>
            seedSegment(prescribed, {
              id: options.newId(),
              sessionExerciseId: exerciseId,
              orderIndex: index,
            }),
          )
        : [];

      exercises.push({
        id: exerciseId,
        sessionId,
        sessionBlockId: blockId,
        exerciseId: exercise.exerciseId,
        orderIndex: exerciseOrder,
        status: 'pending',
        modality: exercise.modality,
        loadType: exercise.loadType,
        // The exercise's own rest wins over the block's; the block is the
        // fallback for a superset that rests only after the round.
        restSeconds: exercise.restSeconds ?? block.restSeconds,
        sourcePlanExerciseId: exercise.planExerciseId,
        prescribed: {
          planExerciseId: exercise.planExerciseId,
          restSeconds: exercise.restSeconds,
          // Copied, not referenced: editing the session must never reach back
          // into the caller's plan data.
          // Carries `targetDurationS` and `targetDistanceM` (C1) unchanged,
          // which is what makes the segment itself need no target columns.
          sets: ordered.map((set) => ({ ...set })),
        },
        sets,
        segments,
      });
    }

    blocks.push({
      id: blockId,
      sessionId,
      orderIndex: block.orderIndex,
      kind: block.kind,
      restSeconds: block.restSeconds,
      sourcePlanBlockId: block.planBlockId,
      exercises,
    });
  }

  return {
    id: sessionId,
    planId: day.planId,
    planDayId: day.planDayId,
    name: options.name ?? day.name,
    status: 'active',
    startedAt: options.startedAt,
    endedAt: null,
    localDate: options.localDate,
    tzOffsetMin: options.tzOffsetMin,
    blocks,
  };
}

/**
 * The next set index for an exercise.
 *
 * Deliberately `max + 1` rather than `count + 1`: removing set 3 of 4 leaves
 * indexes 1, 2, 4, and reusing 4 would collide with the row that is still
 * there. Indexes are identity, not position — position is `displayIndex`.
 */
export function nextSetIndex(sets: readonly { setIndex: number; dropIndex: number }[]): number {
  const primaries = sets.filter((set) => set.dropIndex === 0);
  if (primaries.length === 0) return 1;
  return Math.max(...primaries.map((set) => set.setIndex)) + 1;
}

/**
 * Add a set to an exercise, copying the shape of the one before it.
 *
 * The new set inherits load type and weight from the last primary set, because
 * the overwhelmingly common case is another set at the same weight. Reps are
 * left empty for the same reason they are at materialisation: an unperformed
 * number must not look recorded.
 */
export function buildAddedSet(
  exercise: { id: string; loadType: LoadType; sets: readonly SessionSetDraft[] },
  options: { id: string; setType?: SetType },
): SessionSetDraft {
  const primaries = exercise.sets.filter((set) => set.dropIndex === 0);
  const last = primaries[primaries.length - 1];
  const setIndex = nextSetIndex(exercise.sets);

  return {
    id: options.id,
    sessionExerciseId: exercise.id,
    setIndex,
    dropIndex: 0,
    setType: options.setType ?? 'normal',
    parentSetId: null,

    loadType: exercise.loadType,
    weightG: last?.weightG ?? null,
    bodyweightG: last?.bodyweightG ?? null,
    bodyweightSource: last?.bodyweightSource ?? 'unknown',
    addedWeightG: last?.addedWeightG ?? null,
    assistWeightG: last?.assistWeightG ?? null,

    reps: null,
    durationS: last?.durationS ?? null,
    rpe: null,

    isCompleted: false,
    completedAt: null,

    displayIndex: primaries.length + 1,
  };
}

/**
 * Renumber `displayIndex` after a set is added or removed.
 *
 * Only the display position moves. `setIndex` is never rewritten: it is the
 * identity the stored row and every queued sync event refer to, and shifting it
 * would silently repoint them at a different set.
 */
export function resequenceDisplay(sets: readonly SessionSetDraft[]): SessionSetDraft[] {
  let displayIndex = 0;
  return [...sets]
    .sort((a, b) => a.setIndex - b.setIndex || a.dropIndex - b.dropIndex)
    .map((set) => {
      if (set.dropIndex === 0) displayIndex += 1;
      return { ...set, displayIndex };
    });
}
